/**
 * Single source of truth for resolving how an actor (User or Partea/Team)
 * is displayed across the app: avatar URL, initials, profile route, and
 * story route.
 *
 * Why this module exists:
 *   - Before this, ~10+ components composed avatar URLs inline (`${cdn}/${key}`,
 *     each with their own trailing-slash handling). A few even fell back
 *     to `properties_public.avatar_url`, which is a raw S3 URL that is
 *     not safe to serve to browsers.
 *   - Profile/story navigation was hardcoded in many places, which made
 *     the "click avatar -> story or profile" Instagram contract impossible
 *     to enforce.
 *
 * This module centralizes those decisions. Every avatar surface in the
 * CRA app should call `resolveActorAvatarUrl()` (or use `<ActorAvatar>`
 * which calls it) and `getActorProfileHref()` / `getActorStoryHref()`.
 */

import type { Actor, ActorRef } from "types/actor";
import type { User, Team } from "util/constants";
import type { ChatActorLite, ChatMember, ChatTeamLite } from "types/chat";
import { buildUserStoriesPath } from "components/Stories/paths";

/**
 * IMAGE_CDN base URL with any trailing slash trimmed so we can always
 * concatenate `${IMAGE_CDN}/${key}` safely. Mirrors the normalization
 * pattern already established by `Chat/utils.ts`.
 */
const IMAGE_CDN = String(process.env.REACT_APP_IMAGE_CDN || "").replace(/\/$/, "");

/**
 * Compose the public-facing avatar URL for an actor.
 *
 * Returns `null` when there is no usable `avatar_key` so callers can
 * render the initials fallback via `<AvatarFallback>` (or the
 * `<AvatarRing>` primitive's built-in fallback slot). Refuses to honor
 * `properties_public.avatar_url` even if a caller smuggles it in via a
 * custom `Actor.avatarKey` value: only key-shaped strings + the CDN are
 * trusted for display.
 */
export const resolveActorAvatarUrl = (actor: ActorRef): string | null => {
  if (!actor) {
    return null;
  }
  const key = actor.avatarKey?.trim();
  if (!key) {
    return null;
  }
  if (/^https?:\/\//i.test(key)) {
    // Already a fully-qualified URL (rare, mostly third-party imports).
    // We pass through but never mint one from `avatar_url` ourselves.
    return key;
  }
  if (!IMAGE_CDN) {
    return null;
  }
  return `${IMAGE_CDN}/${key.replace(/^\//, "")}`;
};

/**
 * Pick the best displayable name for an actor. Prefers the trimmed
 * `displayName`; falls back to `username`; finally returns the fallback
 * argument (default "?") when neither is available. Matches the existing
 * Chat helper but accepts the canonical `Actor` shape.
 */
export const getActorDisplayName = (
  actor: ActorRef,
  fallback = "?"
): string => {
  if (!actor) {
    return fallback;
  }
  const displayName = actor.displayName?.trim();
  if (displayName) {
    return displayName;
  }
  if (actor.username?.trim()) {
    return actor.username.trim();
  }
  return fallback;
};

/**
 * Initials for the avatar fallback. Promotes the existing two-letter
 * heuristic from `Chat/utils.ts` so every surface produces the same
 * initials for the same name:
 *
 *   - "John Doe" -> "JD"
 *   - "johndoe"  -> "JO"
 *   - ""         -> "?"
 *
 * Returns uppercase, max 2 characters.
 */
export const getActorInitials = (actor: ActorRef): string => {
  const name = getActorDisplayName(actor, "?");
  const trimmed = name.trim();
  if (!trimmed || trimmed === "?") {
    return "?";
  }
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
};

/**
 * Public-profile URL for an actor. Routes match the CRA route table in
 * `App.tsx`:
 *
 *   - users -> `/user/:username`
 *   - teams -> `/partea/:teamname`
 *
 * Returns `null` when the actor has no usable username slug.
 */
export const getActorProfileHref = (actor: ActorRef): string | null => {
  if (!actor) {
    return null;
  }
  const slug = actor.username?.trim();
  if (!slug) {
    return null;
  }
  if (actor.kind === "team") {
    return `/partea/${encodeURIComponent(slug)}`;
  }
  return `/user/${encodeURIComponent(slug)}`;
};

/**
 * Story-viewer URL for an actor, or `null` when the actor cannot have
 * stories (only users do today; teams/parteas are out of scope per
 * ADR 010).
 *
 * NOTE: this returns a URL even when the actor has no active stories.
 * Whether a ring should be drawn / whether the avatar should link here
 * vs the profile is a separate decision driven by `useActorStoryStatus`.
 */
export const getActorStoryHref = (actor: ActorRef): string | null => {
  if (!actor || actor.kind !== "user") {
    return null;
  }
  if (!Number.isFinite(actor.id) || actor.id <= 0) {
    return null;
  }
  return buildUserStoriesPath(actor.id);
};

// ---------------------------------------------------------------------------
// Coercion helpers — turn any of the existing entity blobs into an `Actor`.
// ---------------------------------------------------------------------------

/**
 * Loose backend-user shape. `properties_public` is intentionally typed
 * as `unknown` (and read defensively below) so callers can hand us
 * domain-specific subtypes — e.g. `UserWithPublicProps` in
 * `UserProfilePlate.tsx` — without TypeScript complaining about the
 * narrower JSON value type.
 */
interface UserShape {
  id?: number | null;
  username?: string | null;
  display_name?: string | null;
  properties_public?: unknown;
}

interface TeamShape {
  id?: number | null;
  teamname?: string | null;
  display_name?: string | null;
  properties_public?: unknown;
}

const readAvatarKey = (properties: unknown): string | null => {
  if (!properties || typeof properties !== "object") {
    return null;
  }
  const raw = (properties as { avatar_key?: unknown }).avatar_key;
  if (typeof raw !== "string") {
    return null;
  }
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
};

/**
 * Build an `Actor` from a backend `User` (or any shape with the same
 * fields). Returns `null` if the input is missing the minimum fields
 * needed to render an avatar (id + username).
 */
export const toActorFromUser = (
  user: UserShape | User | null | undefined
): Actor | null => {
  if (!user) {
    return null;
  }
  const id = typeof user.id === "number" ? user.id : null;
  const username = typeof user.username === "string" ? user.username.trim() : "";
  if (id === null || id <= 0 || !username) {
    return null;
  }
  return {
    id,
    kind: "user",
    username,
    displayName: typeof user.display_name === "string" ? user.display_name : null,
    avatarKey: readAvatarKey(user.properties_public),
  };
};

/**
 * Build an `Actor` from a backend `Team` (Partea). Returns `null` if
 * the input is missing the minimum fields needed.
 */
export const toActorFromTeam = (
  team: TeamShape | Team | null | undefined
): Actor | null => {
  if (!team) {
    return null;
  }
  const id = typeof team.id === "number" ? team.id : null;
  const teamname = typeof team.teamname === "string" ? team.teamname.trim() : "";
  if (id === null || id <= 0 || !teamname) {
    return null;
  }
  return {
    id,
    kind: "team",
    username: teamname,
    displayName: typeof team.display_name === "string" ? team.display_name : null,
    avatarKey: readAvatarKey(team.properties_public),
  };
};

/**
 * Build an `Actor` from a chat member or any "lite" actor row that
 * carries `id`, `username`, `display_name`, and `properties_public`.
 */
export const toActorFromChatActor = (
  member: ChatActorLite | ChatMember | null | undefined
): Actor | null => {
  if (!member) {
    return null;
  }
  // ChatMember exposes both `id` and `user_id`; either one identifies
  // the underlying user. We prefer `id` because that's the one the
  // backend always populates.
  const idCandidate =
    typeof member.id === "number" && member.id > 0
      ? member.id
      : ((member as ChatMember).user_id ?? null);
  const id = typeof idCandidate === "number" ? idCandidate : null;
  const username = typeof member.username === "string" ? member.username.trim() : "";
  if (id === null || id <= 0 || !username) {
    return null;
  }
  return {
    id,
    kind: "user",
    username,
    displayName: typeof member.display_name === "string" ? member.display_name : null,
    avatarKey: readAvatarKey(member.properties_public),
  };
};

/**
 * Build an `Actor` from a chat team-lite row (used by team chat
 * conversations).
 */
export const toActorFromChatTeam = (
  team: ChatTeamLite | null | undefined
): Actor | null => {
  if (!team) {
    return null;
  }
  const id = typeof team.id === "number" ? team.id : null;
  const teamname = typeof team.teamname === "string" ? team.teamname.trim() : "";
  if (id === null || id <= 0 || !teamname) {
    return null;
  }
  return {
    id,
    kind: "team",
    username: teamname,
    displayName: typeof team.display_name === "string" ? team.display_name : null,
    avatarKey: readAvatarKey(team.properties_public),
  };
};

/**
 * Build an `Actor` for a comment author. Comments carry a fairly free-form
 * shape (legacy `Comment` interface in `util/constants.ts`), so we accept
 * any object with the relevant fields. Returns `null` when the comment was
 * authored anonymously or by a deleted user (`user_id === 0`).
 */
export const toActorFromCommentAuthor = (params: {
  user_id?: number | null;
  username?: string | null;
  display_name?: string | null;
  properties_public?: Record<string, unknown> | null;
}): Actor | null => {
  const id = typeof params.user_id === "number" ? params.user_id : null;
  const username = typeof params.username === "string" ? params.username.trim() : "";
  if (id === null || id <= 0 || !username) {
    return null;
  }
  return {
    id,
    kind: "user",
    username,
    displayName: typeof params.display_name === "string" ? params.display_name : null,
    avatarKey: readAvatarKey(params.properties_public),
  };
};
